import { IChatState } from 'src/interface/IChatState';
import { actionTypes, ChatAction } from './chatAction';

const initialState: IChatState = {
  sessions: [],
  activeSessionId: null,
  messages: {},
  loading: false,
  error: null,
  websocketConnected: false,
  typing: false,
  preferences: null,
  isOpen: false,
  unreadCount: 0,
};

// Using any for action type to match codebase pattern and avoid verbose type guards
// ChatAction type exported for type documentation purposes
export default function chatReducer(state = initialState, action: any): IChatState {
  switch (action.type) {
    // Chat widget
    case actionTypes.OPEN_CHAT:
      return {
        ...state,
        isOpen: true,
        unreadCount: 0,
      };

    case actionTypes.CLOSE_CHAT:
      return {
        ...state,
        isOpen: false,
      };

    case actionTypes.TOGGLE_CHAT:
      return {
        ...state,
        isOpen: !state.isOpen,
        unreadCount: !state.isOpen ? 0 : state.unreadCount,
      };

    // Sessions
    case actionTypes.FETCH_SESSIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.FETCH_SESSIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        sessions: action.payload.sessions,
      };

    case actionTypes.FETCH_SESSIONS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case actionTypes.CREATE_SESSION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.CREATE_SESSION_SUCCESS:
      return {
        ...state,
        loading: false,
        sessions: [action.payload.session, ...state.sessions],
        activeSessionId: action.payload.session.id,
        messages: {
          ...state.messages,
          [action.payload.session.id]: [],
        },
      };

    case actionTypes.CREATE_SESSION_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case actionTypes.SET_ACTIVE_SESSION:
      return {
        ...state,
        activeSessionId: action.payload.sessionId,
      };

    // Messages
    case actionTypes.FETCH_MESSAGES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.FETCH_MESSAGES_SUCCESS:
      return {
        ...state,
        loading: false,
        messages: {
          ...state.messages,
          [action.payload.sessionId]: action.payload.messages,
        },
      };

    case actionTypes.FETCH_MESSAGES_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case actionTypes.SEND_MESSAGE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        loading: false,
      };

    case actionTypes.SEND_MESSAGE_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case actionTypes.ADD_MESSAGE: {
      const { sessionId, message } = action.payload;
      const currentMessages = state.messages[sessionId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [sessionId]: [...currentMessages, message],
        },
        unreadCount: !state.isOpen && message.sender_type === 'assistant'
          ? state.unreadCount + 1
          : state.unreadCount,
      };
    }

    case actionTypes.UPDATE_MESSAGE: {
      const { sessionId, messageId, updates } = action.payload;
      const currentMessages = state.messages[sessionId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [sessionId]: currentMessages.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        },
      };
    }

    // WebSocket
    case actionTypes.CONNECT_WEBSOCKET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.CONNECT_WEBSOCKET_SUCCESS:
      return {
        ...state,
        loading: false,
        websocketConnected: true,
      };

    case actionTypes.CONNECT_WEBSOCKET_ERROR:
      return {
        ...state,
        loading: false,
        websocketConnected: false,
        error: action.payload.error,
      };

    case actionTypes.DISCONNECT_WEBSOCKET:
      return {
        ...state,
        websocketConnected: false,
      };

    case actionTypes.SET_TYPING:
      return {
        ...state,
        typing: action.payload.typing,
      };

    // Preferences
    case actionTypes.FETCH_PREFERENCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.FETCH_PREFERENCES_SUCCESS:
      return {
        ...state,
        loading: false,
        preferences: action.payload.preferences,
      };

    case actionTypes.FETCH_PREFERENCES_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case actionTypes.UPDATE_PREFERENCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.UPDATE_PREFERENCES_SUCCESS:
      return {
        ...state,
        loading: false,
        preferences: action.payload.preferences,
      };

    case actionTypes.UPDATE_PREFERENCES_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    // Clear error
    case actionTypes.CLEAR_CHAT_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}
